import { useEffect } from 'react';
import {
  DialogClose,
} from '@/components/ui/dialog';
import { BaseModal } from '@/shared/ui/BaseModal.jsx';
import { Button } from '@/components/ui/button';
import { useDeleteRequest, useGetRequest } from '@/shared/hooks/requests.js';
import { formatFileSize } from '@/shared/utils/helpers.js';
import { NODE, NODE_CHILDREN, NODE_DELETE_PREVIEW } from '@/shared/utils/urls.js';
import { toast } from 'sonner';
import LoadingSpinner from '@/shared/ui/LoadingSpinner.jsx';
import { FileText, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const DeleteFolder = ({ open, onOpenChange, folder, setData }) => {
  const { t } = useTranslation();
  const deleteFolder = useDeleteRequest({});
  const getFolderFiles = useGetRequest({});
  const getPreview = useGetRequest({});

  // On open, fetch a sample of direct children (for the file-name list) plus
  // the subtree preview (the real counts the delete will produce — nested and
  // not page-capped). Reset both when it closes.
  useEffect(() => {
    if (open && folder?.id) {
      getFolderFiles.setResponse(null);
      getPreview.setResponse(null);
      getFolderFiles.request({ url: `${NODE_CHILDREN}?parent_id=${folder.id}` });
      getPreview.request({ url: NODE_DELETE_PREVIEW.replace('{id}', folder.id) });
    } else if (!open) {
      getFolderFiles.setResponse(null);
      getPreview.setResponse(null);
    }
  }, [open, folder?.id]);

  const handleDelete = async () => {
    const { success, response } = await deleteFolder.request({ url: NODE.replace('{id}', folder.id) });
    if (success) {
      // Subtree soft-delete: `deleted` lists every removed node (the folder
      // itself included); signed docs inside were auto-archived instead.
      const deletedCount = Math.max((response?.deleted?.length || 1) - 1, 0);
      toast.success(
        deletedCount > 0
          ? t('my-docs:delete.successWithFiles', { count: deletedCount })
          : t('my-docs:delete.success')
      );
      const archived = response?.archivedSigned?.length || 0;
      if (archived > 0) {
        toast.info(t('my-docs:bulk.archivedSigned', { count: archived }));
      }
      onOpenChange(false);
      setData();
    }
  };

  // Sample of direct child files (names shown); the authoritative totals come
  // from the subtree preview, not this page.
  const files = (getFolderFiles.response?.items || []).filter((n) => n.type === 'file');
  const preview = getPreview.response;
  const totalCount = preview?.total ?? 0;
  const fileCount = preview?.files ?? 0;
  const folderCount = preview?.folders ?? 0;
  const archivedSigned = preview?.archivedSigned ?? 0;
  // Preview finished (not loading) but produced nothing → the request failed
  // (a toast already fired). Show a warning rather than a perpetual spinner;
  // delete stays available since the backend computes the real subtree.
  const previewFailed = !getPreview.loading && preview === null;

  return (
    <BaseModal
      open={open}
      onOpenChange={onOpenChange}
      title={
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          {t('my-docs:delete.title')}
        </div>
      }
      className="max-w-md overflow-hidden"
      footer={
        <div className="flex justify-end gap-3">
          <DialogClose asChild>
            <Button variant="ghost" className="font-medium px-4">{t('my-docs:btn.cancel')}</Button>
          </DialogClose>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteFolder.loading}>
            {deleteFolder.loading ? (
              <LoadingSpinner color="#fff" />
            ) : (
              t('my-docs:btn.delete')
            )}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {t('my-docs:delete.confirm')} <b>{folder?.name}</b>?
        </p>

        {/* Subtree totals (from the delete preview) + a sample of file names */}
        {getPreview.loading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner color="#EF4444" />
          </div>
        ) : previewFailed ? (
          <p className="text-sm text-amber-600">
            {t('my-docs:delete.previewError')}
          </p>
        ) : totalCount > 0 || archivedSigned > 0 ? (
          <div className="border border-red-200 bg-red-50 rounded-md p-3">
            {totalCount > 0 && (
              <p className="text-sm font-medium text-red-800 mb-2">
                {t('my-docs:delete.subtreeWarning', {
                  count: totalCount,
                  files: t('my-docs:delete.fileCount', { count: fileCount }),
                  folders: t('my-docs:delete.folderCount', { count: folderCount }),
                })}
              </p>
            )}
            {archivedSigned > 0 && (
              <p className="text-xs text-red-600 mb-2">
                {t('my-docs:delete.signedArchivedNotice', { count: archivedSigned })}
              </p>
            )}
            {files.length > 0 && (
              <div className="max-h-40 overflow-y-auto space-y-1">
                {files.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-2 text-sm text-red-700 bg-white rounded px-2 py-1"
                  >
                    <FileText className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate flex-1">{file.name}</span>
                    {file.size && (
                      <span className="text-xs text-red-500 flex-shrink-0">
                        {formatFileSize(file.size)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">
            {t('my-docs:delete.noFiles')}
          </p>
        )}
      </div>
    </BaseModal>
  );
};
